import { Button } from "@astryxdesign/core/Button";
import { DateInput } from "@astryxdesign/core/DateInput";
import { NumberInput } from "@astryxdesign/core/NumberInput";
import {
  SegmentedControl,
  SegmentedControlItem,
} from "@astryxdesign/core/SegmentedControl";
import { Section } from "@astryxdesign/core/Section";
import { HStack, StackItem, VStack } from "@astryxdesign/core/Stack";
import { Text } from "@astryxdesign/core/Text";
import { TextInput } from "@astryxdesign/core/TextInput";
import { useToast } from "@astryxdesign/core/Toast";
import { useState } from "react";
import type { Currency, Cycle, SubKind } from "../../../shared/money";
import { api } from "../../lib/api";
import { submitOnEnter } from "../../lib/forms";
import type { IsoDate } from "../../lib/format";
import { PlusIcon, SparkleIcon } from "../../lib/icons";
import { toMinor } from "../../lib/money";
import { useCreateSub } from "../../lib/money-queries";

interface Form {
  kind: SubKind;
  name: string;
  amount: string;
  currency: Currency;
  cycle: Cycle;
  firstDate: string;
  parts: string;
}

const todayIso = () => new Intl.DateTimeFormat("en-CA").format(new Date());

const EMPTY: Form = {
  kind: "recurring",
  name: "",
  amount: "",
  currency: "UAH",
  cycle: "monthly",
  firstDate: todayIso(),
  parts: "9",
};

/**
 * One line of free text → AI-parsed draft → editable form → save. Typing a
 * plain name works too: the form just opens empty for you to fill in.
 */
export function AddSub() {
  const toast = useToast();
  const createSub = useCreateSub();
  const [text, setText] = useState("");
  const [parsing, setParsing] = useState(false);
  const [form, setForm] = useState<Form | null>(null);

  const set = <K extends keyof Form>(key: K, value: Form[K]) =>
    setForm((f) => (f ? { ...f, [key]: value } : f));

  async function parse() {
    const raw = text.trim();
    if (!raw) return;
    setParsing(true);
    try {
      const draft = await api.parseSub(raw);
      setForm({
        kind: draft.kind,
        name: draft.name,
        amount: draft.amount !== null ? String(draft.amount) : "",
        currency: draft.kind === "installment" ? "UAH" : draft.currency,
        cycle: draft.cycle ?? "monthly",
        firstDate: draft.firstDate ?? todayIso(),
        parts: draft.parts !== null ? String(draft.parts) : "9",
      });
    } catch {
      // AI unavailable: open the form with the text as the name.
      setForm({ ...EMPTY, name: raw });
    } finally {
      setParsing(false);
      setText("");
    }
  }

  function save() {
    if (!form) return;
    const name = form.name.trim();
    const amount = toMinor(Number(form.amount.replace(",", ".")));
    if (!name || !Number.isFinite(amount) || amount <= 0) {
      toast({ body: "Name and amount are required", type: "error", uniqueID: "sub-form" });
      return;
    }
    const parts = Number(form.parts);
    createSub.mutate(
      {
        kind: form.kind,
        name,
        amount,
        currency: form.kind === "installment" ? "UAH" : form.currency,
        cycle: form.kind === "recurring" ? form.cycle : null,
        firstDate: form.firstDate,
        parts: form.kind === "installment" ? parts : null,
      },
      {
        onSuccess: () => {
          setForm(null);
          toast({ body: "Added ✓", uniqueID: "sub-added" });
        },
        onError: () =>
          toast({ body: "Could not save", type: "error", uniqueID: "sub-form" }),
      },
    );
  }

  if (!form) {
    return (
      <form
        onSubmit={(e) => {
          e.preventDefault();
          void parse();
        }}
        onKeyDown={submitOnEnter(() => void parse())}
      >
        <TextInput
          label="Add a subscription"
          isLabelHidden
          size="lg"
          placeholder="Netflix 5.49 USD 12 числа — or just a name"
          value={text}
          onChange={setText}
          startIcon={PlusIcon}
          hasClear
          isLoading={parsing}
        />
      </form>
    );
  }

  return (
    <Section padding={3}>
      <VStack gap={3}>
        <HStack gap={1} align="center">
          <SparkleIcon width="1em" height="1em" style={{ color: "var(--color-icon-accent)" }} />
          <Text type="label" color="secondary">
            Check and save
          </Text>
        </HStack>

        <HStack gap={2} wrap="wrap">
          <SegmentedControl
            label="Kind"
            size="sm"
            value={form.kind}
            onChange={(v) => set("kind", v as SubKind)}
          >
            <SegmentedControlItem value="recurring" label="Subscription" />
            <SegmentedControlItem value="installment" label="Installment" />
          </SegmentedControl>
          {form.kind === "recurring" ? (
            <SegmentedControl
              label="Cycle"
              size="sm"
              value={form.cycle}
              onChange={(v) => set("cycle", v as Cycle)}
            >
              <SegmentedControlItem value="monthly" label="Monthly" />
              <SegmentedControlItem value="yearly" label="Yearly" />
            </SegmentedControl>
          ) : null}
        </HStack>

        <TextInput label="Name" size="sm" value={form.name} onChange={(v) => set("name", v)} />

        <HStack gap={2} wrap="wrap" align="end">
          <StackItem size="fill">
            <TextInput
              label={form.kind === "installment" ? "Per part" : "Amount"}
              size="sm"
              value={form.amount}
              onChange={(v) => set("amount", v)}
            />
          </StackItem>
          {form.kind === "recurring" ? (
            <SegmentedControl
              label="Currency"
              size="sm"
              value={form.currency}
              onChange={(v) => set("currency", v as Currency)}
            >
              <SegmentedControlItem value="UAH" label="₴" />
              <SegmentedControlItem value="USD" label="$" />
              <SegmentedControlItem value="EUR" label="€" />
            </SegmentedControl>
          ) : (
            <NumberInput
              label="Parts"
              size="sm"
              value={Number(form.parts)}
              onChange={(v) => set("parts", String(v ?? 1))}
              min={1}
              max={120}
            />
          )}
        </HStack>

        <DateInput
          label={form.kind === "installment" ? "First payment" : "Next charge"}
          size="sm"
          format="date"
          value={form.firstDate as IsoDate}
          onChange={(v) => v && set("firstDate", v)}
        />

        <HStack gap={2} justify="end">
          <Button label="Cancel" variant="ghost" onClick={() => setForm(null)} />
          <Button label="Save" variant="primary" isLoading={createSub.isPending} onClick={save} />
        </HStack>
      </VStack>
    </Section>
  );
}
